import { useMutation } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { usePluginApiClient } from '@/entities/plugin';
import { toast } from '@/shared/ui/sonner';
import { getOAuthProvider } from './providerRegistry';
import { isOAuthReturnMessage } from './oauthPopup';

interface UseConnectProviderOptions {
  /** Fired after the popup reports a successful connection. */
  onConnected?: () => void;
}

class OAuthPopupError extends Error {
  constructor(public reason: 'error' | 'cancelled') {
    super(`oauth-popup-${reason}`);
  }
}

// Navigate the (already-opened) popup to the authorize URL and resolve when it
// relays `connected`, reject on `error` or when the user closes it. The popup is
// opened synchronously in the click handler (see `connect` below) so the browser
// doesn't block it — opening it here, after the `await`, would lose the gesture.
function drivePopup(popup: Window | null, url: string, providerId: string): Promise<void> {
  if (!popup) {
    // ponytail: popup blocked -> full-page redirect. Connect still works but the
    // thread is lost on return; acceptable for the blocked-popup edge. The page
    // unloads, so this promise is intentionally never settled.
    window.location.assign(url);
    return new Promise<void>(() => {});
  }
  try {
    popup.location.href = url;
  } catch {
    window.location.assign(url);
    return new Promise<void>(() => {});
  }
  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      clearInterval(poll);
    };
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      // Bind to this popup so another same-origin tab/window can't spoof a result.
      if (event.source !== popup) return;
      if (!isOAuthReturnMessage(event.data) || event.data.provider !== providerId) return;
      cleanup();
      try {
        popup.close();
      } catch {
        // already closed by the popup itself
      }
      if (event.data.outcome === 'connected') resolve();
      else reject(new OAuthPopupError('error'));
    };
    window.addEventListener('message', onMessage);
    const poll = window.setInterval(() => {
      if (popup.closed) {
        cleanup();
        reject(new OAuthPopupError('cancelled'));
      }
    }, 500);
  });
}

export function useConnectProvider(providerId: string, options: UseConnectProviderOptions = {}) {
  const client = usePluginApiClient();
  const onConnectedRef = useRef(options.onConnected);
  onConnectedRef.current = options.onConnected;

  const mutation = useMutation({
    mutationFn: async (popup: Window | null) => {
      const provider = getOAuthProvider(providerId);
      if (!provider) throw new Error(`Unknown OAuth provider: ${providerId}`);
      const { authorizeUrl } = await client.connectProvider(provider.id);
      const url = new URL(authorizeUrl);
      if (url.protocol !== 'https:' || url.host !== provider.authorizeHost) {
        throw new Error('Invalid authorization URL');
      }
      await drivePopup(popup, url.href, provider.id);
    },
    onSuccess: () => {
      onConnectedRef.current?.();
    },
    onError: (error, popup) => {
      // Close the popup if the flow failed before drivePopup took it over (e.g.
      // the connect request rejected or the authorize URL failed validation),
      // so a backend/URL error doesn't strand an empty about:blank window.
      try {
        popup?.close();
      } catch {
        // already closed
      }
      // A user-cancelled popup is not a failure worth a toast.
      if (error instanceof OAuthPopupError && error.reason === 'cancelled') return;
      const label = getOAuthProvider(providerId)?.label ?? providerId;
      toast.error(`Could not connect ${label}. Please try again.`, { duration: 4000 });
    },
  });

  const connect = useCallback(() => {
    // Open the popup synchronously, inside the click gesture, so the browser
    // treats it as user-initiated; `drivePopup` navigates it after the async
    // authorize-URL fetch. A blank/about:blank popup is safe to reuse.
    const popup = window.open('', `oauth-${providerId}`, 'popup,width=520,height=680');
    mutation.mutate(popup);
  }, [mutation, providerId]);

  return { connect, isPending: mutation.isPending };
}
